// This file was copied from site scanner. Planned to move it to ui-common
import React, { FC, useCallback, useRef, useState } from 'react';

import classNames from 'classnames';

import {
	BUTTON_TYPES,
	CheckIcon,
	CopyIcon,
	ErrorIcon,
	EvButton,
	EvIcon,
	EvToast,
	TOAST_STATUS
} from '@evinced-private/ui-common';

import { ClipboardHelper } from '../../helpers/ClipboardHelper';

import './CopyToClipboard.scss';

const copyStates = {
	copying: {
		text: 'Copying. Please wait',
		icon: null,
		state: TOAST_STATUS.INFO
	},
	success: {
		text: 'Copied to Clipboard',
		icon: CheckIcon,
		state: TOAST_STATUS.SUCCESS
	},
	error: {
		text: 'Copy Attempt Error',
		icon: ErrorIcon,
		state: TOAST_STATUS.FAIL
	}
};

interface ICopyToClipboardProps {
	textToCopy: string;
	copyButtonText?: string;
	renderJustIcon?: boolean;
	className?: string;
	iconTitle?: string;
	buttonType?: BUTTON_TYPES;
	icon?: string;
	renderTooltip?: boolean;
	dataAutomationId?: string;
}

type copyStateItem = {
	text: string;
	icon: string;
	state: TOAST_STATUS;
};

export const CopyToClipboard: FC<ICopyToClipboardProps> = ({
	renderJustIcon,
	textToCopy,
	className,
	renderTooltip,
	dataAutomationId,
	copyButtonText = 'Copy Issue Link',
	iconTitle = 'Copy selector',
	buttonType = BUTTON_TYPES.ACTION,
	icon = CopyIcon
}) => {
	const [copyState, setCopyState] = useState<copyStateItem>(copyStates.copying);
	const [showAnnouncement, setShowAnnouncement] = useState<boolean>(false);
	const [buttonRef, setbuttonRef] = useState(useRef(null));

	const copyTextLinkButton = useRef(null);

	const onToastClose = (): void => {
		setShowAnnouncement(false);
		buttonRef.current.focus();
	};

	const renderToast = (): JSX.Element => (
		<EvToast
			announcement={copyState.text}
			status={copyState.state}
			show={showAnnouncement}
			onToastClose={onToastClose}
		/>
	);

	const copyToClipboard = (text): void => {
		const isCopied = ClipboardHelper.copyToClipboard(text);
		setCopyState(isCopied ? copyStates.success : copyStates.error);
		setShowAnnouncement(true);
	};

	const copyButtonSelector = useCallback(
		(copyToClipboardButton: React.RefObject<HTMLButtonElement>): void => {
			setbuttonRef(copyToClipboardButton);
			copyToClipboard(textToCopy);
		},
		[textToCopy]
	);

	const copyTextLink = useCallback((): void => {
		setbuttonRef(copyTextLinkButton);
		copyToClipboard(textToCopy);
	}, [textToCopy]);

	return (
		<div className={classNames('copy-to-clipboard', className)}>
			{showAnnouncement && renderToast()}
			<div id="announce" className="sr-only" aria-live="assertive">
				{copyState.text !== copyStates.copying.text && <p>{copyState.text}</p>}
			</div>
			<div className="copy-text" data-automation-id={dataAutomationId}>
				{renderJustIcon ? (
					<EvButton
						ref={buttonRef}
						type={BUTTON_TYPES.ICON}
						onClick={() => copyButtonSelector(buttonRef)}
						title={iconTitle}
						ariaLabel={iconTitle}
						customTooltip={{ renderTooltip }}
					>
						<EvIcon icon={icon} />
					</EvButton>
				) : (
					<EvButton
						ref={copyTextLinkButton}
						type={buttonType}
						title={copyButtonText}
						onClick={copyTextLink}
					>
						{copyButtonText}
					</EvButton>
				)}
			</div>
		</div>
	);
};
